/**
 * 🔐 PasskeyNudge — one-time (per 30 days) prompt to enroll Face ID
 *
 * Renders a bottom-anchored slide-in banner when ALL of the following are true:
 *   • User is authenticated
 *   • Device supports platform authenticator (Face ID / Touch ID / etc.)
 *   • User has ZERO passkeys already registered
 *   • The nudge hasn't been dismissed in the last 30 days
 *
 * CTA jumps to /profile#face-id (the ManagePasskeys section) and scrolls
 * to it. Dismissal is per-user via localStorage.
 */
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Fingerprint, X, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { isPasskeySupported, isPlatformAuthenticatorAvailable, listMyPasskeys } from "@/lib/passkeys";

const SNOOZE_DAYS = 30;
const SNOOZE_MS = SNOOZE_DAYS * 24 * 3600 * 1000;

function storageKey(userId) {
  return `omni:passkey-nudge:${userId || "anon"}`;
}

function isSnoozed(userId) {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return false;
    const t = parseInt(raw, 10);
    if (!Number.isFinite(t)) return false;
    return Date.now() - t < SNOOZE_MS;
  } catch {
    return false;
  }
}

function snoozeNow(userId) {
  try {
    localStorage.setItem(storageKey(userId), String(Date.now()));
  } catch { /* noop */ }
}

export default function PasskeyNudge() {
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [visible, setVisible] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Guard rails
      if (!user || checking) return;
      if (!isPasskeySupported()) return;
      // Don't stack the nudge on top of the Profile page — user is already there.
      if (loc.pathname === "/profile") return;
      if (isSnoozed(user.user_id)) return;

      setChecking(true);
      try {
        const platformOk = await isPlatformAuthenticatorAvailable();
        if (cancelled || !platformOk) return;
        const data = await listMyPasskeys().catch(() => ({ passkeys: [] }));
        if (cancelled) return;
        if ((data?.passkeys || []).length > 0) return; // already has one
        // Wait a beat so the nudge doesn't fight the toast queue on page load
        setTimeout(() => { if (!cancelled) setVisible(true); }, 2500);
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, loc.pathname]);

  if (!visible) return null;

  const dismiss = () => {
    snoozeNow(user?.user_id);
    setVisible(false);
  };

  const goEnroll = () => {
    snoozeNow(user?.user_id);
    setVisible(false);
    nav("/profile#face-id");
  };

  return (
    <div
      data-testid="passkey-nudge"
      className="fixed left-1/2 bottom-24 z-[60] -translate-x-1/2 w-[min(94vw,440px)] pointer-events-auto animate-in fade-in slide-in-from-bottom-4"
      style={{ animation: "slideUp 400ms cubic-bezier(0.16, 1, 0.3, 1)" }}
    >
      <div className="relative bg-gradient-to-br from-[#0F0F0F] to-[#1a0d2b] border-2 border-[#D4AF37] shadow-[0_10px_40px_rgba(212,175,55,0.35)] p-4">
        <button
          data-testid="passkey-nudge-dismiss"
          onClick={dismiss}
          className="absolute top-2 right-2 text-neutral-500 hover:text-white p-1"
          aria-label="Dismiss"
        >
          <X size={16} />
        </button>
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-11 h-11 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37] flex items-center justify-center">
            <Fingerprint size={22} className="text-[#D4AF37]" />
          </div>
          <div className="flex-1 min-w-0 pr-4">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              Face ID Ready
            </div>
            <h4 className="text-white font-display text-lg leading-tight mt-0.5">
              Never type your password again.
            </h4>
            <p className="text-neutral-300 text-xs leading-relaxed mt-1.5">
              Enable a passkey on this device — sign in with a glance instead of a keyboard.
            </p>
            <div className="flex gap-2 mt-3">
              <button
                data-testid="passkey-nudge-enroll"
                onClick={goEnroll}
                className="flex-1 bg-[#D4AF37] hover:bg-amber-300 text-black uppercase tracking-widest text-[11px] font-bold py-2 px-3 flex items-center justify-center gap-1.5 transition-colors"
              >
                Enable Face ID <ArrowRight size={12} />
              </button>
              <button
                data-testid="passkey-nudge-later"
                onClick={dismiss}
                className="text-neutral-400 hover:text-white text-[11px] uppercase tracking-widest px-3"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes slideUp {
          from { transform: translate(-50%, 24px); opacity: 0; }
          to   { transform: translate(-50%, 0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
}
